import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { applyCleaningJob } from '@/components/cleaning/cache'
import { useToast } from '@/hooks/use-toast'

export interface UndoActionProps {
  propertyId: string
  jobId: string
  /** What is being taken back, for the confirmation toast: "Ack recorded". */
  describe: string
  /** The job it happened to, so the confirmation still names it. */
  context: string
  /** Refresh whatever the action changed, once the job is back as it was. */
  onUndone: () => void
  /**
   * admin-api refused for good (409): something else has written the job
   * since, so this undo will never be available again.
   */
  onRefused?: () => void
}

/**
 * Take back the last action on one job (CAP-511).
 *
 * It calls the backend rather than reversing anything here. admin-api holds a
 * snapshot of the job as it stood before the action and puts that back, which
 * is the only thing that can restore state the action destroyed - and it is
 * the backend's own guard that decides whether undo is still available, not a
 * timer in the console. A 409 means something else has written the job since,
 * so there is no longer one write to take back; that is said plainly rather
 * than retried.
 *
 * Shared by the toast's Undo and the Undo on a settled row (CAP-785), so the
 * two say the same thing either way. Both offer the same undo of the same job,
 * so `busy` is true while EITHER is in flight: two undos racing would have the
 * loser report a refusal for an undo that in fact went through.
 */
export function useUndoCleaningAction({
  propertyId,
  jobId,
  describe,
  context,
  onUndone,
  onRefused,
}: UndoActionProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const mutationKey = ['cleaning', 'undo', propertyId, jobId]
  const inFlight = useIsMutating({ mutationKey }) > 0

  const mutation = useMutation({
    mutationKey,
    mutationFn: () => api.runCleaningJobAction(propertyId, jobId, 'undo'),
    onSuccess: data => {
      // NOT lower-cased: several of these carry a crew's name ("Assigned
      // Alesia"), and a blanket toLowerCase turns a person into "alesia".
      toast({ title: `Undone: ${describe}`, description: context })
      // The restored job as admin-api put it back, so the lists stop showing
      // the undone state while `onUndone`'s refetches are still out.
      applyCleaningJob(queryClient, data.job)
      onUndone()
    },
    onError: (error: Error & { status?: number }) => {
      toast({
        title: 'Could not undo that',
        description: error.message,
        variant: 'destructive',
      })
      if (error.status === 409) onRefused?.()
    },
  })
  return { undo: () => mutation.mutate(), busy: mutation.isPending || inFlight }
}
